import { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  DueDiligenceCategory,
  DueDiligenceQuestion,
  DueDiligenceAnswerOption,
  DueDiligenceResponse,
  DueDiligenceScope,
  CreateDueDiligenceCategoryDto,
  UpdateDueDiligenceCategoryDto,
  CreateDueDiligenceQuestionDto,
  UpdateDueDiligenceQuestionDto,
  CreateDueDiligenceAnswerOptionDto,
  UpdateDueDiligenceAnswerOptionDto,
  UpsertDueDiligenceResponseDto,
  UpsertBulkDueDiligenceResponsesDto,
  CategoryDotValues,
} from 'shared/types/due-diligence';

// ============================================
// Categories Hooks
// ============================================

interface UseDueDiligenceCategoriesReturn {
  categories: DueDiligenceCategory[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDueDiligenceCategories = (scope?: DueDiligenceScope): UseDueDiligenceCategoriesReturn => {
  const { session } = useSession();
  const [categories, setCategories] = useState<DueDiligenceCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    if (!session) return;

    try {
      setIsLoading(true);
      setError(null);

      const url = scope
        ? `${API_URL}/due-diligence-categories?scope=${scope}`
        : `${API_URL}/due-diligence-categories`;

      const response = await fetchWithAuth(url, { method: 'GET' }, session);

      if (!response.ok) {
        throw new Error(`Kon due diligence categorieën niet ophalen: ${response.status}`);
      }

      const data = await response.json();
      setCategories(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon due diligence categorieën niet ophalen';
      setError(errorMessage);
      console.error('Error fetching due diligence categories:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, scope]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    isLoading,
    error,
    refetch: fetchCategories,
  };
};

interface UseDueDiligenceCategoryReturn {
  category: DueDiligenceCategory | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDueDiligenceCategory = (id: number | string | null): UseDueDiligenceCategoryReturn => {
  const { session } = useSession();
  const [category, setCategory] = useState<DueDiligenceCategory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategory = useCallback(async () => {
    if (!session || !id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/${id}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Kon due diligence categorie niet ophalen: ${response.status}`);
      }

      const data = await response.json();
      setCategory(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon due diligence categorie niet ophalen';
      setError(errorMessage);
      console.error('Error fetching due diligence category:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, id]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  return {
    category,
    isLoading,
    error,
    refetch: fetchCategory,
  };
};

// ============================================
// Category Mutations
// ============================================

interface UseCreateDueDiligenceCategoryReturn {
  createCategory: (data: CreateDueDiligenceCategoryDto) => Promise<DueDiligenceCategory>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateDueDiligenceCategory = (): UseCreateDueDiligenceCategoryReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCategory = useCallback(async (data: CreateDueDiligenceCategoryDto): Promise<DueDiligenceCategory> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon categorie niet aanmaken: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon categorie niet aanmaken';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return { createCategory, isCreating, error };
};

interface UseUpdateDueDiligenceCategoryReturn {
  updateCategory: (id: number, data: UpdateDueDiligenceCategoryDto) => Promise<DueDiligenceCategory>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateDueDiligenceCategory = (): UseUpdateDueDiligenceCategoryReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateCategory = useCallback(async (id: number, data: UpdateDueDiligenceCategoryDto): Promise<DueDiligenceCategory> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/${id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon categorie niet bijwerken: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon categorie niet bijwerken';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return { updateCategory, isUpdating, error };
};

interface UseDeleteDueDiligenceCategoryReturn {
  deleteCategory: (id: number) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteDueDiligenceCategory = (): UseDeleteDueDiligenceCategoryReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteCategory = useCallback(async (id: number): Promise<void> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/${id}`,
        { method: 'DELETE' },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon categorie niet verwijderen: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon categorie niet verwijderen';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return { deleteCategory, isDeleting, error };
};

// ============================================
// Question Mutations
// ============================================

interface UseCreateDueDiligenceQuestionReturn {
  createQuestion: (categoryId: number, data: CreateDueDiligenceQuestionDto) => Promise<DueDiligenceQuestion>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateDueDiligenceQuestion = (): UseCreateDueDiligenceQuestionReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createQuestion = useCallback(async (
    categoryId: number,
    data: CreateDueDiligenceQuestionDto
  ): Promise<DueDiligenceQuestion> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/${categoryId}/questions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon vraag niet aanmaken: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon vraag niet aanmaken';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return { createQuestion, isCreating, error };
};

interface UseUpdateDueDiligenceQuestionReturn {
  updateQuestion: (categoryId: number, questionId: number, data: UpdateDueDiligenceQuestionDto) => Promise<DueDiligenceQuestion>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateDueDiligenceQuestion = (): UseUpdateDueDiligenceQuestionReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateQuestion = useCallback(async (
    categoryId: number,
    questionId: number,
    data: UpdateDueDiligenceQuestionDto
  ): Promise<DueDiligenceQuestion> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/${categoryId}/questions/${questionId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon vraag niet bijwerken: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon vraag niet bijwerken';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return { updateQuestion, isUpdating, error };
};

interface UseDeleteDueDiligenceQuestionReturn {
  deleteQuestion: (categoryId: number, questionId: number) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteDueDiligenceQuestion = (): UseDeleteDueDiligenceQuestionReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteQuestion = useCallback(async (categoryId: number, questionId: number): Promise<void> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/${categoryId}/questions/${questionId}`,
        { method: 'DELETE' },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon vraag niet verwijderen: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon vraag niet verwijderen';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return { deleteQuestion, isDeleting, error };
};

// ============================================
// Answer Option Mutations
// ============================================

interface UseCreateDueDiligenceAnswerOptionReturn {
  createAnswerOption: (categoryId: number, questionId: number, data: CreateDueDiligenceAnswerOptionDto) => Promise<DueDiligenceAnswerOption>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateDueDiligenceAnswerOption = (): UseCreateDueDiligenceAnswerOptionReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createAnswerOption = useCallback(async (
    categoryId: number,
    questionId: number,
    data: CreateDueDiligenceAnswerOptionDto
  ): Promise<DueDiligenceAnswerOption> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/${categoryId}/questions/${questionId}/options`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon antwoordoptie niet aanmaken: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon antwoordoptie niet aanmaken';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return { createAnswerOption, isCreating, error };
};

interface UseUpdateDueDiligenceAnswerOptionReturn {
  updateAnswerOption: (categoryId: number, questionId: number, optionId: number, data: UpdateDueDiligenceAnswerOptionDto) => Promise<DueDiligenceAnswerOption>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateDueDiligenceAnswerOption = (): UseUpdateDueDiligenceAnswerOptionReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateAnswerOption = useCallback(async (
    categoryId: number,
    questionId: number,
    optionId: number,
    data: UpdateDueDiligenceAnswerOptionDto
  ): Promise<DueDiligenceAnswerOption> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/${categoryId}/questions/${questionId}/options/${optionId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon antwoordoptie niet bijwerken: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon antwoordoptie niet bijwerken';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return { updateAnswerOption, isUpdating, error };
};

interface UseDeleteDueDiligenceAnswerOptionReturn {
  deleteAnswerOption: (categoryId: number, questionId: number, optionId: number) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteDueDiligenceAnswerOption = (): UseDeleteDueDiligenceAnswerOptionReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteAnswerOption = useCallback(async (
    categoryId: number,
    questionId: number,
    optionId: number
  ): Promise<void> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/${categoryId}/questions/${questionId}/options/${optionId}`,
        { method: 'DELETE' },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon antwoordoptie niet verwijderen: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon antwoordoptie niet verwijderen';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return { deleteAnswerOption, isDeleting, error };
};

// ============================================
// Response Hooks
// ============================================

interface UseDueDiligenceResponsesReturn {
  responses: DueDiligenceResponse[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDueDiligenceResponses = (
  buildingId?: string,
  unitId?: string,
  categoryId?: number
): UseDueDiligenceResponsesReturn => {
  const { session } = useSession();
  const [responses, setResponses] = useState<DueDiligenceResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchResponses = useCallback(async () => {
    if (!session || (!buildingId && !unitId)) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let url: string;
      if (unitId) {
        url = `${API_URL}/due-diligence-responses/unit/${unitId}`;
      } else {
        url = `${API_URL}/due-diligence-responses/building/${buildingId}`;
      }

      if (categoryId) {
        url += `?categoryId=${categoryId}`;
      }

      const response = await fetchWithAuth(url, { method: 'GET' }, session);

      if (!response.ok) {
        throw new Error(`Kon due diligence antwoorden niet ophalen: ${response.status}`);
      }

      const data = await response.json();
      setResponses(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon due diligence antwoorden niet ophalen';
      setError(errorMessage);
      console.error('Error fetching due diligence responses:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, buildingId, unitId, categoryId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  return {
    responses,
    isLoading,
    error,
    refetch: fetchResponses,
  };
};

// ============================================
// Response Mutations
// ============================================

interface UseUpsertDueDiligenceResponseReturn {
  upsertResponse: (data: UpsertDueDiligenceResponseDto) => Promise<DueDiligenceResponse>;
  isUpserting: boolean;
  error: string | null;
}

export const useUpsertDueDiligenceResponse = (): UseUpsertDueDiligenceResponseReturn => {
  const { session } = useSession();
  const [isUpserting, setIsUpserting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upsertResponse = useCallback(async (data: UpsertDueDiligenceResponseDto): Promise<DueDiligenceResponse> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsUpserting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-responses`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon antwoord niet opslaan: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon antwoord niet opslaan';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpserting(false);
    }
  }, [session]);

  return { upsertResponse, isUpserting, error };
};

interface UseUpsertBulkDueDiligenceResponsesReturn {
  upsertBulkResponses: (data: UpsertBulkDueDiligenceResponsesDto) => Promise<DueDiligenceResponse[]>;
  isUpserting: boolean;
  error: string | null;
}

export const useUpsertBulkDueDiligenceResponses = (): UseUpsertBulkDueDiligenceResponsesReturn => {
  const { session } = useSession();
  const [isUpserting, setIsUpserting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upsertBulkResponses = useCallback(async (data: UpsertBulkDueDiligenceResponsesDto): Promise<DueDiligenceResponse[]> => {
    if (!session) {
      throw new Error('Geen sessie beschikbaar');
    }

    try {
      setIsUpserting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-responses/bulk`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Kon antwoorden niet opslaan: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon antwoorden niet opslaan';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpserting(false);
    }
  }, [session]);

  return { upsertBulkResponses, isUpserting, error };
};

// ============================================
// Dot Values Hooks (for Dashboard)
// ============================================

interface UseDueDiligenceDotValuesReturn {
  dotValues: CategoryDotValues[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDueDiligenceDotValues = (
  buildingId?: string,
  unitId?: string,
  categoryId?: number
): UseDueDiligenceDotValuesReturn => {
  const { session } = useSession();
  const [dotValues, setDotValues] = useState<CategoryDotValues[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDotValues = useCallback(async () => {
    if (!session || (!buildingId && !unitId)) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let url: string;
      if (unitId) {
        url = `${API_URL}/due-diligence-responses/unit/${unitId}/dot-values`;
      } else {
        url = `${API_URL}/due-diligence-responses/building/${buildingId}/dot-values`;
      }

      if (categoryId) {
        url += `?categoryId=${categoryId}`;
      }

      const response = await fetchWithAuth(url, { method: 'GET' }, session);

      if (!response.ok) {
        throw new Error(`Kon due diligence status niet ophalen: ${response.status}`);
      }

      const data = await response.json();
      setDotValues(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kon due diligence status niet ophalen';
      setError(errorMessage);
      console.error('Error fetching due diligence dot values:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, buildingId, unitId, categoryId]);

  useEffect(() => {
    fetchDotValues();
  }, [fetchDotValues]);

  return {
    dotValues,
    isLoading,
    error,
    refetch: fetchDotValues,
  };
};
